/**
 * Main menu functionality for responsive behaviors and dropdowns.
 *
 * @package  Embark
 * @since    1.0.0
 */

/**
 * Import dependencies.
 */
import $ from 'jquery';
import hoverintent from 'hoverintent';
import { disableScroll, enableScroll, isElementInViewport } from '../global/helpers';
import { els, breakpoints } from './variables';

/**
 * Check if the site should be displaying the mobile menu or not.
 */
export function isMobileMenu() {
	return breakpoints.menu.belowOrTouch();
}

/**
 * Check if the site should be displaying the desktop menu or not.
 */
export function isDesktopMenu() {
	return !isMobileMenu();
}

export class ResponsiveMenu {
	hoverIntentTimeout = 200;

	constructor({
		menu, navicon, container, menuOpenNaviconClass, menuOpenHtmlClass, menuItemTriggerClass,
	}) {
		if (menu && navicon && container && menuOpenNaviconClass && menuOpenHtmlClass && menuItemTriggerClass) {
			// The menu element.
			this.menu = menu;
			// The button that open's and closes the menu on mobile/touch devices.
			this.navicon = navicon;
			// The element that hidden/shown when the navicon is clicked.
			this.container = container;
			// THe class to add to the navicon element when the mobile menu is open.
			this.menuOpenNaviconClass = menuOpenNaviconClass;
			// The class to add to the html element when the mobile menu is open.
			this.menuOpenHtmlClass = menuOpenHtmlClass;
			// The class of the element that, when clicked, opens its sibling .sub-menu element.
			this.menuItemTriggerClass = menuItemTriggerClass;

			this.init();
		}
	}

	/**
	 * Set some initial menu item classes and all events.
	 */
	init = () => {
		// Add event listeners to menu items that have children.
		this.parentItems = this.menu.querySelectorAll('.menu-item-has-children');
		if (this.parentItems.length) {
			this.parentItems.forEach((item) => {
				hoverintent(item, () => {
					item.classList.add('hover');
				}, () => {
					item.classList.remove('hover');
				}).options({
					timeout: this.hoverIntentTimeout,
				});

				item.addEventListener('mouseenter', this.handleOffscreenSubmenus);
				item.addEventListener('click', this.handleTriggerClick);

				// Expand current menu items on mobile.
				if (item.matches('[class*="current"]') && isMobileMenu()) {
					const submenu = item.querySelector('.sub-menu');
					item.classList.add('open');
					submenu.style.display = 'block';
				}
			});
		}

		// Add class to the first .current-menu-ancestor to avoid multiple being highlighted.
		this.ancestorItems = document.querySelectorAll('.main-menu > .current-menu-ancestor');
		if (this.ancestorItems.length > 0) {
			this.ancestorItems[0].classList.add('current-menu-ancestor--primary');
		}

		window.addEventListener('resize', this.handleResize);
		document.addEventListener('click', this.handleMenuState);
	}

	/**
	 * Check if current submenu is fully within the viewport.
	 * If not, add a class so that it can be styled accordingly.
	 *
	 * Note: 'this' refers to the menu item the event is added to,
	 * not the MainMenu class instance.
	 */
	handleOffscreenSubmenus() {
		const submenu = this.querySelector('.sub-menu');
		if (submenu && !isElementInViewport(submenu) && isDesktopMenu()) {
			this.classList.add('sub-menu--offscreen');
		}
	}

	/**
	 * Toggle mobile submenus when a menu item link with a hash or the
	 * menu item trigger is clicked.
	 *
	 * Note: 'this' refers to the menu item the event is added to,
	 * not the MainMenu class instance.
	 *
	 * @param  {Event} e
	 */
	handleTriggerClick = (e) => {
		// Prevents further propagation of the current event in the capturing and bubbling phases.
		e.stopPropagation();

		// Get the submenu based on what was clicked.
		let submenu;
		const { target } = e;
		if (target.href) {
			// Handle links that have # hashes. These should open the child submenu when clicked.
			const hash = target.href.substring(target.href.lastIndexOf('/') + 1);
			if (hash && hash === '#') {
				submenu = target.nextElementSibling.nextElementSibling;
			}
		} else if (target.classList.contains(this.menuItemTriggerClass)) {
			submenu = target.nextElementSibling;
		}

		// If we have a submenu, expand it.
		if (submenu) {
			e.preventDefault();

			const $submenu = $(submenu);
			const $menuItem = $submenu.closest('.menu-item');

			$menuItem.find('> a').attr('aria-expanded', (index, attr) => (attr === 'true' ? 'false' : 'true'));
			$menuItem.toggleClass('open');

			if (isMobileMenu()) {
				$submenu.slideToggle();
			}
		}
	}

	/**
	 * Update mobile menu top offset and close opened submenus depending on screensize.
	 */
	handleResize = () => {
		window.requestAnimationFrame(() => {
			if (isDesktopMenu()) {
				this.closeMobileMenu();
				this.closeOpenSubmenus();
			}
		});
	}

	/**
	 * Open the mobile menu.
	 */
	openMobileMenu = () => {
		disableScroll();
		els.html.classList.add(this.menuOpenHtmlClass);
		this.navicon.classList.add(this.menuOpenNaviconClass);
	}

	/**
	 * Close the mobile menu.
	 */
	closeMobileMenu = () => {
		// Make sure there isn't a lightbox open before we enable scrolling.
		if (!els.html.classList.contains('lightbox-open')) {
			enableScroll();
		}

		els.html.classList.remove(this.menuOpenHtmlClass);
		this.navicon.classList.remove(this.menuOpenNaviconClass);
	}

	/**
	 * Toggle the mobile menu.
	 */
	toggleMobileMenu = () => {
		if (els.html.classList.contains(this.menuOpenHtmlClass)) {
			this.closeMobileMenu();
		} else {
			this.openMobileMenu();
		}
	}

	/**
	 * Close the mobile menu when you click off of it.
	 *
	 * @param  {Event} e
	 */
	handleMenuState = (e) => {
		// If the menu is open, and the event target is the nav or a child of the nav, exit quickly.
		if (els.html.classList.contains(this.menuOpenHtmlClass) && (e.target === this.container || $(e.target).closest(this.container).length > 0)) {
			return;
		}

		// If event target is the navicon or any of its children, trigger the toggle method.
		if (e.target === this.navicon || $(e.target).closest(this.navicon).length > 0) {
			this.toggleMobileMenu();
		} else {
			// Else, this is not the navicon trigger. Close the menu. User has clicked away.
			this.closeMobileMenu();
		}
	}

	/**
	 * Close all open submenus.
	 */
	closeOpenSubmenus = () => {
		const openMenuItems = this.menu.querySelectorAll('.menu-item.open');
		if (openMenuItems) {
			openMenuItems.forEach((menuItem) => {
				menuItem.classList.remove('open');
				const submenus = menuItem.querySelectorAll('.sub-menu');
				submenus.forEach((submenu) => {
					submenu.style.display = '';
				});
			});
		}
	}
}

if (els.mainMenu) {
	// eslint-disable-next-line no-unused-vars
	const mainMenu = new ResponsiveMenu({
		menu: els.mainMenu,
		navicon: els.navicon,
		container: els.navContainer,
		menuOpenNaviconClass: 'navicon--open',
		menuOpenHtmlClass: 'html--menu-open',
		menuItemTriggerClass: 'menu-item__trigger',
	});
}
